<template>
  <div class="tabs">
    <div ref="header" class="header" :style="headerStyles">
      <div class="scrollable-tabs-container">
        <div
          v-for="(item, index) of items"
          ref="item"
          :key="index"
          class="header__title"
          @click="$emit('select', item.id)"
        >
          <slot name="tab-title" :item="item">{{ item.title }}</slot>
        </div>

        <div class="scrollable-tabs-container__active-underline"></div>
      </div>
    </div>

    <div class="tabs__body">
      <slot></slot>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

import { types } from '@/store/types';

export default {
  name: 'AppTabs',
  props: {
    items: {
      type: Array,
      required: true
    },
    selectedItemId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      tabGeometryProps: [],
      doTabsFitHeaderSpace: true
    };
  },
  computed: {
    ...mapState({
      locale: types.state.LOCALE
    }),
    selectedItemIndex() {
      return this.items.findIndex(item => item.id === this.selectedItemId);
    },
    headerStyles() {
      const offset = this.tabPositions[this.selectedItemIndex];
      let headerShift = 0;

      if (!this.doTabsFitHeaderSpace) {
        const leftOffset = offset !== 0 ? 80 : 0; // make a bit of previous tab visible
        headerShift = -1 * (offset - leftOffset);
      }

      const { width: activeTabWidth } = this.tabGeometryProps[this.selectedItemIndex] || {};

      const underlineShift = offset !== 0 ? offset : 0;
      const directionFactor = this.locale === 'ar' ? -1 : 1;

      return {
        '--header-shift': `${headerShift * directionFactor}px`,
        '--underline-width': `${activeTabWidth}px`,
        '--underline-shift': `${underlineShift * directionFactor}px`
      };
    },
    tabPositions() {
      const coords = this.tabGeometryProps.reduce((acc, item, i) => {
        const prevWidth = acc[i - 1] || 0;

        return [...acc, prevWidth + item.width];
      }, []);
      coords.pop();
      coords.unshift(0);

      return coords;
    }
  },
  async mounted() {
    // prevent getting wrong geometry props from tabs
    await this.$nextTick();
    await this.$nextTick();
    this.initTabsGeometryProps();

    window.addEventListener('resize', this.initTabsGeometryProps);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.initTabsGeometryProps);
  },
  methods: {
    initTabsGeometryProps() {
      this.tabGeometryProps = this.$refs.item.map(item => item.getBoundingClientRect());

      const { width: headerWidth } = this.$refs.header.getBoundingClientRect();
      const totalTabsWidth = this.tabGeometryProps.reduce((acc, item) => acc + item.width, 0);

      this.doTabsFitHeaderSpace = totalTabsWidth <= headerWidth;
    }
  }
};
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/colors.scss';

.tabs {
  &__body {
    padding-top: 32px;
  }
}

.header {
  font-family: var(--u-font-family-Helvetica);
  font-size: 16px;
  line-height: 20px;
  position: relative;
  z-index: 0;
  width: 100%;
  overflow-x: hidden;

  &__title {
    padding-bottom: 19px;
    cursor: pointer;
    z-index: 1;
    white-space: nowrap;
  }

  &__title:not(:last-child) {
    padding-inline-end: 32px;
  }

  &::before {
    content: '';
    display: block;
    width: 100%;
    height: 0;
    position: absolute;
    bottom: 0;
    border-bottom: 1px solid #bdb9b2;
    z-index: 0;

    &:not(:lang(ar)) {
      left: 0;
    }

    &:lang(ar) {
      right: 0;
    }
  }
}

.scrollable-tabs-container {
  display: flex;
  transform: translateX(var(--header-shift));
  transition: transform 0.3s ease-in;
  position: relative;

  &__active-underline {
    position: absolute;
    height: 0;
    border-top: 2px solid $tuatara-color;
    width: var(--underline-width);
    transform: translateX(var(--underline-shift));
    transition: transform 0.3s ease-in, width 0.3s ease-in;
    transition-delay: 0.1s;
    bottom: 0;

    &:not(:lang(ar)) {
      left: 0;
    }

    &:lang(ar) {
      right: 0;
    }
  }
}
</style>
